import React, { useEffect, useState, useRef } from "react"
import { useParams, useNavigate, useLocation } from "react-router-dom"
import { Button, Card, CardBody, Col, Row, Spinner } from "reactstrap"
import { InputText } from "primereact/inputtext"
import { Editor } from "@tinymce/tinymce-react"
import * as Yup from "yup"
import "./EditPolicy.scss"
import { Toast } from "primereact/toast"
import PolicyApi from "Api/PolicyApi"
import CustomBreadCrumb from "utils/CustomBreadCrumb/CustomBreadCrumb"

const EditPolicy = () => {
  document.title = "Edit Policy"
  const { id } = useParams()
  const location = useLocation()
  const editData = location.state?.data || {}
  const navigate = useNavigate()
  const accessToken = localStorage.getItem("accessToken")

  const [formData, setFormData] = useState({
    policyName: "",
    policyDescription: "",
    policyContent: "",
  })
  const [loading, setLoading] = useState(true)
  const [saveLoading, setSaveLoading] = useState(false)
  const [errors, setErrors] = useState({})
  const toast = useRef(null)

  const validationSchema = Yup.object().shape({
    policyName: Yup.string().required("Policy name is required"),
    policyDescription: Yup.string().required("Policy description is required"),
    policyContent: Yup.string().required("Policy content is required"),
  })

  const getPolicy = () => {
    setLoading(true)
    const config = {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    }

    PolicyApi.getPolicy(config)
      .then(response => {
        const policies = response.data.data || []
        const policy = policies.find(p => p._id === id) || editData
        
        if (policy) {
          setFormData({
            policyName: policy.policyName || "",
            policyDescription: policy.policyDescription || "",
            policyContent: policy.policyContent || "",
          })
        }
        
        if (toast.current) {
          toast.current.show({
            severity: "success",
            summary: "Success",
            detail: "Policy fetched successfully",
            life: 3000,
          })
        }
        setLoading(false)
      })
      .catch(error => {
        console.error("Error fetching policy:", error)
        if (toast.current) {
          toast.current.show({
            severity: "error",
            summary: "Error",
            detail:
              error.response?.data?.message || "Failed to fetch policy",
            life: 3000,
          })
        }
        setLoading(false)
      })
  }

  useEffect(() => {
    getPolicy()
  }, [id, accessToken])

  const handleInputChange = (name, value) => {
    setFormData(prevData => ({
      ...prevData,
      [name]: value,
    }))
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined,
      }))
    }
  }

  const handleSubmit = () => {
    setSaveLoading(true)
    validationSchema
      .validate(formData, { abortEarly: false })
      .then(() => {
        const config = {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }

        PolicyApi.updatePolicy(id, formData, config)
          .then(response => {
            if (response.data) {
              setSaveLoading(false)
              setTimeout(() => {
                navigate(-1)
              }, 1000)
              toast.current.show({
                severity: "success",
                summary: "Success",
                detail: "Policy updated successfully",
                life: 3000,
              })
            }
          })
          .catch(error => {
            console.error("Error updating policy:", error)
            toast.current.show({
              severity: "error",
              summary: "Error",
              detail:
                error.response?.data?.message ||
                "Failed to update policy",
              life: 3000,
            })
            setSaveLoading(false)
          })
      })
      .catch(err => {
        const validationErrors = {}
        err.inner.forEach(error => {
          validationErrors[error.path] = error.message
        })
        setErrors(validationErrors)
        toast.current.show({
          severity: "error",
          summary: "Error",
          detail: "Please fill all required fields correctly!",
          life: 3000,
        })
        setSaveLoading(false)
      })
  }

  const breadcrumbItems = [
    { label: "Pixwik", link: "/" },
    { label: "Policy", link: "/Policy" },
    { label: "Edit Policy", link: "#" },
  ]
  
  const handleBreadcrumbClick = link => {
    if (link !== "#") {
      navigate(link)
    }
  }

  if (loading) {
    return (
      <div className="text-center mt-5">
        <Spinner />
      </div>
    )
  }

  return (
    <>
      <Toast ref={toast} position="top-right" life="3000" />
      <div className="main-content-container">
        <Row>
          <Col xs="12">
            <div className="policy-header d-flex align-items-center justify-content-between">
              <CustomBreadCrumb
                data={breadcrumbItems}
                pageTitle="Edit Policy"
                handleClick={handleBreadcrumbClick}
              />
            </div>
            <Card>
              <CardBody>
                <div className="policy-edit-form">
                  <div className="form-group">
                    <label htmlFor="policyName" className="label">
                      Policy Name
                    </label>
                    <InputText
                      id="policyName"
                      value={formData.policyName}
                      onChange={e => handleInputChange("policyName", e.target.value)}
                      className={`w-100 ${errors.policyName ? "p-invalid" : ""}`}
                      placeholder="Enter policy name"
                    />
                    {errors.policyName && (
                      <small className="p-error">{errors.policyName}</small>
                    )}
                  </div>

                  <div className="form-group">
                    <label htmlFor="policyDescription" className="label">
                      Policy Description
                    </label>
                    <textarea
                      id="policyDescription"
                      value={formData.policyDescription}
                      onChange={e => handleInputChange("policyDescription", e.target.value)}
                      className={`form-control ${errors.policyDescription ? "is-invalid" : ""}`}
                      rows="4"
                      placeholder="Enter policy description"
                    />
                    {errors.policyDescription && (
                      <div className="invalid-feedback">{errors.policyDescription}</div>
                    )}
                  </div>

                  <div className="form-group">
                    <label htmlFor="policyContent" className="label">
                      Policy Content
                    </label>
                    <Editor
                      apiKey="t7g4d6ut5ygdvwif7tp5bwyysui56413aomjuh8kusvw5qgv"
                      value={formData.policyContent}
                      onEditorChange={content => handleInputChange("policyContent", content)}
                      init={{
                        height: 500,
                        menubar: true,
                        plugins: [
                          "advlist",
                          "autolink",
                          "lists",
                          "link",
                          "image",
                          "charmap",
                          "preview",
                          "anchor",
                          "code",
                          "fullscreen",
                          "table",
                          "help",
                          "wordcount",
                        ],
                        toolbar:
                          "undo redo | formatselect | " +
                          "bold italic backcolor | alignleft aligncenter " +
                          "alignright alignjustify | bullist numlist outdent indent | " +
                          "removeformat | code | help",
                        content_css: false,
                        content_style: "",
                        valid_elements: "*[*]",
                        extended_valid_elements: "style,link[href|rel]",
                        custom_elements: "~style",
                        verify_html: false,
                        cleanup: false,
                        convert_fonts_to_spans: false,
                        remove_trailing_brs: false,
                        force_br_newlines: false,
                        force_p_newlines: false,
                        forced_root_block: false,
                        valid_children: "+body[style],+html[style]",
                        element_format: "html",
                        entities_encode: false,
                        entity_encoding: "raw",
                        preserve_cdata: true,
                        protect: [
                          /<![\s\S]*?>/g,
                          /<\?[\s\S]*?\?>/g,
                          /<style[\s\S]*?style>/g,
                        ],
                        valid_styles: {
                          "*": "*",
                        },
                        paste_as_text: false,
                        paste_enable_default_filters: false,
                        setup: function (editor) {
                          editor.on("BeforePaste", function (e) {
                            e.preventDefault()
                            const text =
                              e.clipboardData.getData("text/html") ||
                              e.clipboardData.getData("text")
                            editor.execCommand("mceInsertContent", false, text)
                          })
                        },
                      }}
                    />
                    {errors.policyContent && (
                      <small className="p-error">{errors.policyContent}</small>
                    )}
                  </div>

                  <div className="form-actions">
                    <Button
                      color="primary"
                      onClick={handleSubmit}
                      disabled={saveLoading}
                      className="me-2"
                    >
                      {saveLoading ? (
                        <>
                          <Spinner size="sm" className="me-2" />
                          Saving...
                        </>
                      ) : (
                        "Save Changes"
                      )}
                    </Button>
                    <Button
                      color="secondary"
                      onClick={() => navigate(-1)}
                      disabled={saveLoading}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>
    </>
  )
}

export default EditPolicy